import React, { useEffect, useMemo, useState } from 'react';
import type { DeclaracaoNaoAssociacao, Negociacao, UnidadeConsumidora } from '../../../../types';
import { useAuth } from '../../../../src/contexts/AuthContext';
import { useToast } from '../../../../src/contexts/ToastContext';
import { logUsageEvent } from '../../../../src/lib/logger';
import { fetchSignatariosDefault } from '../../../../src/lib/termoSignatarios';
import { atualizarDeclaracao, emitirDeclaracao } from '../../../../src/services/declaracaoNaoAssociacao';
import { montarDeclaracao, podeEmitirDeclaracao } from '../../../../supabase/functions/_shared/declaracaoNaoAssociacao.ts';
import DeclaracaoNaoAssociacaoCard from './DeclaracaoNaoAssociacaoCard';

interface DeclaracaoNaoAssociacaoPanelProps {
    negociacao: Negociacao;
    ucs: UnidadeConsumidora[];
    /** Vem do useDeclaracoesNaoAssociacao no DetailLeftColumn — a mais recente primeiro. */
    declaracoes: DeclaracaoNaoAssociacao[];
    /** Recarrega as declarações e a negociação depois de emitir/atualizar. */
    onChange: () => void;
}

/** Liga o card ao app: sessão, permissão, procurador configurado e a edge function.
 * Aparece só em negociação reprovada por crédito (o DetailLeftColumn decide). */
const DeclaracaoNaoAssociacaoPanel: React.FC<DeclaracaoNaoAssociacaoPanelProps> = ({ negociacao, ucs, declaracoes, onChange }) => {
    const { user, profile } = useAuth();
    const { addToast } = useToast();
    const [acao, setAcao] = useState<'emitir' | 'atualizar' | null>(null);
    const [procuradorNome, setProcuradorNome] = useState('o procurador');

    useEffect(() => {
        let cancelado = false;
        fetchSignatariosDefault().then(s => {
            if (!cancelado && s.procurador?.nome) setProcuradorNome(s.procurador.nome);
        });
        return () => { cancelado = true; };
    }, []);

    const podeEmitir = podeEmitirDeclaracao(profile?.role, user?.id, negociacao.responsavel_id);

    // O servidor confere tudo de novo; aqui só antecipa o caso que a tela já conhece.
    const avalistaAtivo = negociacao.avalistas?.some(a => a.status !== 'removido');
    const bloqueio = avalistaAtivo
        ? 'Há um avalista em andamento. Remova o avalista antes de emitir a declaração.'
        : null;

    // Mesmo texto que a edge function imprime, com os dados atuais da negociação.
    const previa = useMemo(() => montarDeclaracao({
        clienteNome: negociacao.cliente_nome || '',
        cpfCnpj: negociacao.cpf_cnpj || '',
        ucs: [...ucs]
            .sort((a, b) => Number(a.id ?? 0) - Number(b.id ?? 0))
            .map(u => String(u.numero_uc || u.outro_numero_uc || '').trim())
            .filter(Boolean),
        termoAssinadoEm: negociacao.data_assinatura_termo ?? null,
        procuradorNome,
        emitidaEm: new Date(),
    }), [negociacao.cliente_nome, negociacao.cpf_cnpj, negociacao.data_assinatura_termo, ucs, procuradorNome]);

    const handleEmitir = async (): Promise<boolean> => {
        setAcao('emitir');
        try {
            const r = await emitirDeclaracao(negociacao.id);
            if (r.erro) {
                addToast(r.erro, 'error');
                return false;
            }
            addToast(r.aviso || 'Declaração emitida e assinada. A negociação está encerrada.', r.aviso ? 'warning' : 'success');
            logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'emitir_declaracao_nao_associacao', origem: 'acao' });
            onChange();
            return true;
        } finally {
            setAcao(null);
        }
    };

    const handleAtualizar = async (declaracao: DeclaracaoNaoAssociacao) => {
        setAcao('atualizar');
        try {
            const r = await atualizarDeclaracao(declaracao.id);
            if (r.erro) addToast(r.erro, 'error');
            else if (r.aviso) addToast(r.aviso, 'warning');
            else addToast('Declaração assinada. O PDF já está disponível.', 'success');
            onChange();
        } finally {
            setAcao(null);
        }
    };

    const handleCopiar = (url: string) => {
        navigator.clipboard.writeText(url);
        addToast('Link do PDF copiado!', 'success');
    };

    return (
        <DeclaracaoNaoAssociacaoCard
            declaracoes={declaracoes}
            podeEmitir={podeEmitir}
            bloqueio={bloqueio}
            previa={previa}
            procuradorNome={procuradorNome}
            acao={acao}
            onEmitir={handleEmitir}
            onAtualizar={handleAtualizar}
            onCopiarLink={handleCopiar}
        />
    );
};

export default DeclaracaoNaoAssociacaoPanel;
